import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useNavigate } from "../app/navigation.js";
import { Eye, ArrowLeft, Gauge, Pause, Play, RotateCcw, SkipBack, SkipForward } from "lucide-react";
import { rankLabel, sortHand, type Card, type Seat } from "@guandan/engine";
import { parseReplayArchiveV2, type ReplayArchiveV2 } from "@guandan/protocol";
import { CardFace } from "../art/Card.js";
import { EventScheduler } from "../game/scheduler.js";
import { fanPlacement, mobileLiveLayout, replayLayout, type ReplayLayout, type TableLayout } from "../game/layout.js";
import { SeatRing } from "../game/SeatRing.js";
import { useSize } from "../game/useSize.js";
import { buildReplayStream, type ReplayHands } from "../game/replaySource.js";
import { ApiError, fetchOwnReplay, fetchReplayCard, fetchSharedReplay, type ReplayCard } from "../net/api.js";
import { BottomSheet, Button, EmptyState, Spinner } from "../ui/primitives.js";
import { useLocale } from "../i18n.js";
import "./table.css";
import "./replays.css";
import { isWidePath } from "../app/widePath.js";

const SPEEDS = [0.5, 1, 2, 4] as const;

const minutes = (milliseconds: number) =>
  `${Math.max(1, Math.round(milliseconds / 60_000))}m`;

interface ReplayHandGeometry {
  readonly horizontal: boolean;
  readonly columns: 1 | 2;
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
  readonly columnGap: number;
}

/**
 * Give every hand its own lane on a phone.
 *
 * Deriving all four hands independently from their avatars let the top row
 * cross both side columns. Portrait replay instead reserves a narrow top row,
 * two edge columns, and a wider bottom row, with a clear centre for the trick.
 */
function replayHandGeometry(
  table: TableLayout,
  box: TableLayout["seats"][number],
  size: { width: number; height: number },
): ReplayHandGeometry {
  if (table.mode === "portrait") {
    switch (box.relative) {
      case 0:
        return {
          horizontal: true,
          columns: 1,
          left: table.width * 0.12,
          top: table.height * 0.62,
          width: table.width * 0.76,
          height: table.height * 0.16,
          columnGap: 0,
        };
      case 1:
        return {
          horizontal: false,
          columns: 1,
          left: table.width * 0.24,
          top: table.height * 0.295,
          width: size.width,
          height: table.height * 0.27,
          columnGap: 0,
        };
      case 2:
        return {
          horizontal: true,
          columns: 1,
          left: table.width * 0.19,
          top: Math.max(78, table.height * 0.22) - size.height / 2,
          width: table.width * 0.62,
          height: size.height,
          columnGap: 0,
        };
      case 3:
        return {
          horizontal: false,
          columns: 1,
          left: table.width * 0.76 - size.width,
          top: table.height * 0.295,
          width: size.width,
          height: table.height * 0.27,
          columnGap: 0,
        };
    }
  }

  const replayTable = table as TableLayout & Partial<ReplayLayout>;
  const lane = replayTable.replayLanes?.find((entry) => entry.relative === box.relative);
  if (lane !== undefined) {
    return {
      horizontal: lane.direction === "horizontal",
      columns: lane.columns,
      left: lane.left,
      top: lane.top,
      width: lane.width,
      height: lane.height,
      columnGap: lane.columnGap,
    };
  }

  const horizontal = box.relative === 0 || box.relative === 2;
  return {
    horizontal,
    columns: 1,
    left: horizontal ? table.width * 0.21 : box.relative === 1 ? box.x : box.x - size.width,
    top: horizontal ? box.y - size.height / 2 : table.height * 0.20,
    width: horizontal ? table.width * 0.58 : size.width,
    height: horizontal ? size.height : table.height * 0.60,
    columnGap: 0,
  };
}

/** A complete face-up hand placed just inside its player's avatar. */
function VisibleReplayHand({
  seat,
  cards,
  table,
  level,
  label,
}: {
  seat: Seat;
  cards: readonly Card[];
  table: TableLayout;
  level: Parameters<typeof CardFace>[0]["level"];
  label: string;
}) {
  if (cards.length === 0) return null;
  const box = table.seats.find((entry) => entry.seat === seat);
  if (box === undefined) return null;

  if (table.mode === "portrait" && box.relative === 0) {
    const ordered = sortHand([...cards], level);
    return <div className="replay-hand replay-hand-0" role="group" aria-label={label}>
      {ordered.map((card, index) => {
        const place = fanPlacement(index, ordered.length, table.fan, table.cardWidth);
        return <CardFace key={card} card={card} level={level} width={table.cardWidth} height={table.cardHeight}
          style={{ position: "absolute", left: place.x, top: place.y, transform: "translate(-50%, -50%)", zIndex: index + 2 }} />;
      })}
    </div>;
  }

  const size = { width: table.cardWidth, height: table.cardHeight };
  const geometry = replayHandGeometry(table, box, size);
  const { horizontal } = geometry;
  const ordered = sortHand([...cards], level);
  const columnCount = geometry.columns;
  const columns = columnCount === 2
    ? [ordered.slice(0, Math.ceil(ordered.length / 2)), ordered.slice(Math.ceil(ordered.length / 2))]
    : [ordered];
  const maxCardsInColumn = Math.max(...columns.map((column) => column.length), 1);
  const step = maxCardsInColumn <= 1
    ? 0
    : horizontal
      ? Math.min(size.width * 0.31, (geometry.width - size.width) / (maxCardsInColumn - 1))
      : Math.min(size.height * 0.55, (geometry.height - size.height) / (maxCardsInColumn - 1));
  const rowSpan = step * Math.max(0, maxCardsInColumn - 1);
  const totalColumnWidth = columns.length * size.width + Math.max(0, columns.length - 1) * geometry.columnGap;

  return (
    <div className={`replay-hand replay-hand-${box.relative}`} role="group" aria-label={label}>
      {columns.map((column, columnIndex) => column.map((card, index) => {
        const left = horizontal
          ? geometry.left + geometry.width / 2 - rowSpan / 2 + step * index
          : geometry.left + (geometry.width - totalColumnWidth) / 2 + columnIndex * (size.width + geometry.columnGap);
        const top = horizontal
          ? geometry.top + (geometry.height - size.height) / 2
          : geometry.top + (geometry.height - rowSpan - size.height) / 2 + step * index;
        return (
          <CardFace
            key={`${seat}-${columnIndex}-${card}`}
            card={card}
            level={level}
            width={size.width}
            height={size.height}
            style={{
              position: "absolute",
              left,
              top,
              zIndex: index + 2,
              transform: horizontal ? "translateX(-50%)" : undefined,
            }}
          />
        );
      }))}
    </div>
  );
}

/** Full-screen playback of a finished match, inside either the app or public shell. */
export function ReplayViewer({ shared = false, wide = false }: { shared?: boolean; wide?: boolean }) {
  const { t, number, date, locale } = useLocale();
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const wideRoute = wide || isWidePath(location.pathname);
  const surface = useSize();

  const [archive, setArchive] = useState<ReplayArchiveV2 | null>(null);
  const [card, setCard] = useState<ReplayCard | null>(null);
  const [failed, setFailed] = useState(false);
  const [seat, setSeat] = useState<Seat | null>(null);
  const [playing, setPlaying] = useState(true);
  const [handsOpen, setHandsOpen] = useState(false);
  const [speed, setSpeed] = useState<number>(1);
  const [cursor, setCursor] = useState(0);
  const [scrub, setScrub] = useState<number | null>(null);

  useEffect(() => {
    const id = shared ? params.slug ?? "" : params.roomId ?? "";
    setArchive(null);
    setCard(null);
    setFailed(false);
    setSeat(null);
    setPlaying(true);
    setCursor(0);
    setScrub(null);
    if (id === "") { setFailed(true); return; }
    let cancelled = false;
    void (async () => {
      try {
        const [raw, meta] = await Promise.all([
          shared ? fetchSharedReplay(id) : fetchOwnReplay(id),
          fetchReplayCard(id, shared).catch(() => null),
        ]);
        if (cancelled) return;
        const parsed = parseReplayArchiveV2(new TextEncoder().encode(JSON.stringify(raw)));
        setCard(meta);
        setArchive(parsed);
        // Participants keep their own seat at the bottom. Public viewers use
        // seat 0 as the neutral orientation; every hand is visible either way.
        setSeat(parsed.viewerSeat ?? null);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [shared, params.slug, params.roomId]);

  const stream = useMemo(
    () => (archive === null ? null : buildReplayStream(archive, seat)),
    [archive, seat],
  );
  const scheduler = useMemo(() => new EventScheduler(seat), [seat, archive]);
  const snapshot = useSyncExternalStore(scheduler.subscribe, scheduler.getSnapshot, scheduler.getSnapshot);
  const cursorRef = useRef(0);
  const [timelineRevision, setTimelineRevision] = useState(0);

  useEffect(() => { cursorRef.current = 0; setCursor(0); }, [scheduler]);
  useEffect(() => () => scheduler.detach(), [scheduler]);

  useEffect(() => {
    if (stream === null || !playing) return;
    let timer = 0;
    let cancelled = false;
    const scheduleNext = () => {
      if (cancelled) return;
      const index = cursorRef.current;
      if (index >= stream.events.length) { setPlaying(false); return; }
      const delay = stream.delays[index] ?? 0;
      timer = window.setTimeout(() => {
        if (cancelled) return;
        scheduler.ingest([stream.events[index] as never]);
        cursorRef.current = index + 1;
        setCursor(index + 1);
        scheduleNext();
      }, Math.max(16, delay / speed));
    };
    scheduleNext();
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [stream, playing, speed, scheduler, timelineRevision]);

  const seekTo = useCallback((target: number, resume = false) => {
    if (stream === null) return;
    const clamped = Math.max(0, Math.min(stream.events.length, target));
    scheduler.seek(stream.events.slice(0, clamped) as never[]);
    cursorRef.current = clamped;
    setCursor(clamped);
    setScrub(null);
    // Cancel the timer that captured a pre-seek index before scheduling from the new cursor.
    setTimelineRevision((revision) => revision + 1);
    if (resume) setPlaying(true);
  }, [stream, scheduler]);

  const model = snapshot.model;
  const latestAction = model.activity.at(-1) ?? null;
  const [actionPulse, setActionPulse] = useState<{
    seq: number;
    seat: Seat;
    kind: "played" | "passed";
  } | null>(null);

  useEffect(() => {
    if (latestAction === null) {
      setActionPulse(null);
      return;
    }
    const pulse = {
      seq: latestAction.seq,
      seat: latestAction.seat,
      kind: latestAction.kind,
    } as const;
    setActionPulse(pulse);
    const timer = window.setTimeout(() => {
      setActionPulse((current) => current?.seq === pulse.seq ? null : current);
    }, 1_100);
    return () => window.clearTimeout(timer);
  }, [latestAction?.seq, latestAction?.seat, latestAction?.kind]);

  const table = useMemo(
    () => wideRoute ? replayLayout(Math.max(1, surface.width), Math.max(1, surface.height), seat, true) : mobileLiveLayout(Math.max(1, surface.width), Math.max(1, surface.height), seat),
    [surface.width, surface.height, seat, wideRoute],
  );

  if (failed) {
    return (
      <div className="replay-interstitial">
        <EmptyState
          title={t("replayGone")}
          hint={t("tryAgain")}
          action={
            <Button variant="primary" onClick={() => navigate(shared ? "/" : "/record?tab=matches")}>
              {shared ? t("signInPlay") : t("backLobby")}
            </Button>
          }
        />
      </div>
    );
  }
  if (archive === null || stream === null) {
    return <div className="replay-interstitial"><Spinner label={t("loading")} /></div>;
  }

  const playerFor = (target: Seat) => card?.players.find((entry) => entry.seat === target) ?? null;
  const nameFor = (target: Seat) => playerFor(target)?.name ?? `Seat ${target + 1}`;
  const visibleActions = model.trickActions.length > 0 ? model.trickActions : model.lastTrickActions;
  const latestBySeat = [...visibleActions].reduce<Map<Seat, (typeof visibleActions)[number]>>(
    (actions, action) => {
      actions.set(action.seat, action);
      return actions;
    },
    new Map(),
  );
  const total = stream.events.length;
  const position = scrub ?? cursor;
  const atEnd = cursor >= total;
  const currentHand = Math.max(1, stream.handStarts.filter((start) => start <= position).length);
  const currentHandIndex = currentHand - 1;
  const visibleHands: ReplayHands = stream.handsAt[Math.min(cursor, stream.handsAt.length - 1)]
    ?? [[], [], [], []];
  const title = card === null ? t("replays") : date(card.endedAt);
  const replayGeometry = table as TableLayout & Partial<ReplayLayout>;
  const trickSafe = replayGeometry.trickSafe ?? {
    left: table.width * 0.16,
    top: table.height * 0.32,
    width: table.width * 0.68,
    height: table.height * 0.30,
  };

  const jumpToHand = (index: number) => {
    const target = stream.handStarts[index];
    if (target !== undefined) seekTo(target);
  };

  return (
    <section className={`replay-viewer${wideRoute ? " replay-viewer-wide" : ""}`} aria-label={t("replays")}>
      <div className="replay-stage">
        <header className={`replay-viewer-head${shared ? " replay-viewer-head-shared" : ""}`}>
          {!shared ? (
            <button
              type="button"
              className="replay-icon-button"
              aria-label={t("backLobby")}
              onClick={() => navigate(`${wideRoute ? "/w" : ""}/record?tab=matches`)}
            >
              <ArrowLeft aria-hidden="true" />
            </button>
          ) : <span className="replay-head-spacer" />}

          <div className="replay-title">
            <h1>{title}</h1>
            <p>
              {card !== null && (
                <span className={`replay-mode replay-mode-${card.mode}`}>
                  {card.mode === "ranked" ? t("ranked") : t("goldMode")}
                </span>
              )}
              <span className="replay-hand-number">{t("handNumber", { count: number(currentHand) })}</span>
              {card !== null && <span className="replay-duration">{minutes(card.durationMs)}</span>}
            </p>
          </div>

          <div className="replay-levels" aria-label={t("level")}>
            <span className="team-a">A {rankLabel(model.levels[0])}</span>
            <span className="team-b">B {rankLabel(model.levels[1])}</span>
          </div>
        </header>

        <div className="replay-surface" ref={surface.ref}>
          {surface.width > 0 && (
            <>
              <SeatRing
                table={table}
                entryFor={(target) => ({
                  name: nameFor(target),
                  avatarId: playerFor(target)?.avatarId ?? "avatar-01",
                  expression: actionPulse?.seat === target
                    ? actionPulse.kind === "passed" ? "pass" : "play"
                    : "idle",
                  onTurn: actionPulse?.seat !== target && model.turn === target && model.phase !== "handOver" && model.phase !== "matchOver",
                  badge: model.out[target] ? "—" : model.counts[target],
                })}
              />

              {(wideRoute ? [0, 1, 2, 3] as Seat[] : [(seat ?? 0) as Seat]).map((target) => (
                <VisibleReplayHand
                  key={target}
                  seat={target}
                  cards={visibleHands[target]}
                  table={table}
                  level={model.level}
                  label={`${nameFor(target)} · ${visibleHands[target].length}`}
                />
              ))}

              <div
                className={`replay-play-zone${latestAction === null ? " replay-play-zone-empty" : ""}`}
                style={{ left: table.trickX, top: table.trickY, width: Math.min(176, trickSafe.width * 0.78) }}
                aria-label={latestAction === null ? t("noActivity") : `${nameFor(latestAction.seat)} · ${latestAction.kind === "passed" ? t("pass") : latestAction.combo?.label ?? ""}`}
              >
                <span>{latestAction === null ? t("currentTrick") : nameFor(latestAction.seat)}</span>
                <strong>{latestAction === null ? t("noActivity") : latestAction.kind === "passed" ? t("pass") : latestAction.combo?.label ?? ""}</strong>
              </div>

              {table.seats.map((box) => {
                const action = latestBySeat.get(box.seat);
                if (action === undefined || action.kind !== "passed") return null;
                const active = actionPulse?.seq === action.seq;
                return (
                  <div
                    key={`replay-action-${action.seq}`}
                    className={`replay-action-marker replay-action-marker-${box.relative} replay-action-marker-pass${active ? " replay-action-marker-active" : ""}`}
                    style={{ left: box.playX, top: box.playY }}
                  >
                    {t("pass").toUpperCase()}
                  </div>
                );
              })}

              {model.current !== null && (
                <div
                  className="trick replay-trick"
                  style={{ left: table.trickX, top: table.trickY, width: table.trickCardWidth + (model.current.combo.cards.length - 1) * table.trickCardWidth * 0.48, height: table.trickCardHeight }}
                >
                  {model.current.combo.cards.map((playedCard, index) => (
                    <CardFace
                      key={`${playedCard}-${index}`}
                      card={playedCard}
                      level={model.level}
                      width={table.trickCardWidth}
                      height={table.trickCardHeight}
                      style={{ position: "absolute", left: index * table.trickCardWidth * 0.48, top: 0 }}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <BottomSheet open={handsOpen} onClose={() => setHandsOpen(false)} title={locale === "zh-CN" ? "回放手牌" : "Replay hands"}>
        {([0, 1, 2, 3] as Seat[]).map(target => <section key={target} className="replay-hand-detail">
          <h3>{nameFor(target)} · {visibleHands[target].length}</h3>
          <div>{sortHand([...visibleHands[target]], model.level).map(card => <CardFace key={card} card={card} level={model.level} width={44} height={62} />)}</div>
        </section>)}
      </BottomSheet>
      <footer className="replay-player">
        {!wideRoute && <Button className="replay-hands-trigger" size="sm" variant="ghost" onClick={() => { setPlaying(false); setHandsOpen(true); }}><Eye size={20} aria-hidden="true" />{locale === "zh-CN" ? "查看手牌" : "View hands"}</Button>}

        <div className="replay-progress-row">
          <span className="replay-time-label tabular">{number(currentHand)}</span>
          <div className="replay-track">
            <input
              type="range"
              className="replay-scrubber"
              min={0}
              max={total}
              step={1}
              value={position}
              aria-label={t("timeline")}
              aria-valuetext={t("handNumber", { count: number(currentHand) })}
              onChange={(event) => setScrub(Number(event.currentTarget.value))}
              onPointerUp={() => scrub !== null && seekTo(scrub)}
              onKeyUp={() => scrub !== null && seekTo(scrub)}
              onBlur={() => scrub !== null && seekTo(scrub)}
            />
            <div className="replay-ticks" aria-hidden="true">
              {stream.handStarts.map((start, index) => (
                <i key={index} style={{ left: `${total === 0 ? 0 : (start / total) * 100}%` }} />
              ))}
            </div>
          </div>
          <span className="replay-time-label tabular">{number(stream.hands)}</span>
        </div>

        <div className="replay-player-row">
          <div className="replay-transport" role="group" aria-label={t("timeline")}>
            <button
              type="button"
              className="replay-icon-button"
              disabled={currentHandIndex <= 0}
              aria-label={t("previousMoment")}
              onClick={() => jumpToHand(currentHandIndex - 1)}
            >
              <SkipBack aria-hidden="true" />
            </button>
            <button
              type="button"
              className="replay-play"
              aria-label={atEnd ? t("restart") : playing ? t("playbackPause") : t("playbackPlay")}
              onClick={() => atEnd ? seekTo(0, true) : setPlaying((value) => !value)}
            >
              {atEnd
                ? <RotateCcw aria-hidden="true" />
                : playing
                  ? <Pause aria-hidden="true" fill="currentColor" />
                  : <Play aria-hidden="true" fill="currentColor" />}
            </button>
            <button
              type="button"
              className="replay-icon-button"
              disabled={currentHandIndex >= stream.handStarts.length - 1}
              aria-label={t("nextMoment")}
              onClick={() => jumpToHand(currentHandIndex + 1)}
            >
              <SkipForward aria-hidden="true" />
            </button>
          </div>

          <div className="replay-speed" role="radiogroup" aria-label={t("playbackSpeed")}>
            <Gauge aria-hidden="true" />
            {SPEEDS.map((option) => (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={speed === option}
                className={speed === option ? "is-active" : ""}
                onClick={() => setSpeed(option)}
              >
                {option}×
              </button>
            ))}
          </div>
        </div>
      </footer>
    </section>
  );
}

export { ApiError };
